import type { PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Layout } from "antd";
import { createStyles } from "antd-style";

import { logout } from "@av/apps/common-apps/platform/services/authentication/authentication";
import { ResponsiveHeader } from "@av/components/organisms/ResponsiveHeader";
import { WebHeader } from "@av/components/organisms/WebHeader";
import { useFetchHrPermissions } from "../services/admin-settings/admin-settings";
import { useCurrentUser } from "../services/authentication/user/current-user";
import { useIsDemoExperience } from "../services/demo-experience/demo";

const useStyles = createStyles(
  (
    { token, css },
    {
      isEdgeToEdge,
      additionalLeftMargin,
    }: {
      isEdgeToEdge: boolean;
      additionalLeftMargin: number;
    }
  ) => {
    let marginLeft = isEdgeToEdge ? 0 : 70;

    marginLeft += isNaN(additionalLeftMargin) ? 0 : additionalLeftMargin;

    return {
      layoutContent: css`
        margin-top: 64px;
        padding: ${token.padding}px;
        height: calc(100vh - 64px);
        box-sizing: border-box;
        background: transparent;
        position: relative;
        margin-left: ${marginLeft}px;
        transition: margin-left ${token.motionDurationMid} ${token.motionEaseInOut};
        overflow: auto;
        @media (max-width: ${token.screenMDMax}px) {
          margin-left: 0;
          margin-top: 0;
          height: 100vh;
        }
      `,
    };
  }
);

export function GenericPlatformLayout({ children }: PropsWithChildren) {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { data: currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const { styles } = useStyles({
    isEdgeToEdge: true,
    additionalLeftMargin: 70,
  });
  const isDemo = useIsDemoExperience();
  const { data: permissions = [] } = useFetchHrPermissions(false);

  return (
    <Layout style={{ flexGrow: 1 }}>
      {isMobile ? (
        <ResponsiveHeader
          isDemo={isDemo}
          user={currentUser}
          apps={[]}
          currentApp="hr"
          onAppChange={() => {}}
          navItems={[]}
          permissions={permissions}
        />
      ) : (
        <WebHeader
          isDemo={isDemo}
          subMenuExpandedWidth={70}
          user={currentUser}
          zIndex={1}
          apps={[]}
          permissions={permissions}
          currentApp="hr"
          onAppChange={() => {}}
          navItems={[]}
          isSubmenuOpen={false}
          onLogout={async () => {
            try {
              await logout();
            } catch {
              console.warn("Logging out failed");
            } finally {
              navigate("/login");
            }
          }}
          avaButton={null}
        />
      )}
      <Layout.Content className={styles.layoutContent}>{children}</Layout.Content>
    </Layout>
  );
}
